// services/templateService.js
// API service for template management
import { apifetch } from './interceptors';
// const API_BASE_URL = 'https://app.pipsqueaknetscan.com/s3/api';
const API_BASE_URL = '/s3/api';

/**
 * Get auth headers with token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Get available templates for report generation
 * This is the single source of truth for fetching templates
 */
export const getAvailableTemplates = async () => {
  const data = await fetchAvailableTemplates();
  return data.templates;
};

/**
 * Get the templates that can render one specific scan.
 * The backend reads the scan's version header and leaves out templates built for a
 * different scanner major version (e.g. v1 templates for a v2 scan).
 * @returns {Promise<{templates: Array, scanSchemaVersion: number|null, hiddenCount: number}>}
 */
export const getTemplatesForScan = async (fileUrl) => {
  const data = await fetchAvailableTemplates(fileUrl);
  return {
    templates: data.templates,
    scanSchemaVersion: data.scanSchemaVersion ?? null,
    hiddenCount: data.hiddenCount || 0,
  };
};

const fetchAvailableTemplates = async (fileUrl = null) => {
  try {
    const query = fileUrl ? `?fileUrl=${encodeURIComponent(fileUrl)}` : '';
    const response = await apifetch(`${API_BASE_URL}/available-templates${query}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      // Handle 502 Bad Gateway specifically
      if (response.status === 502) {
        console.error('[getAvailableTemplates] 502 Bad Gateway - Backend service may be unavailable');
        // Try to extract error message from response
        let errorMessage = 'Backend service is temporarily unavailable. Please try again in a moment.';
        try {
          const errorText = await response.text();
          // If it's HTML (nginx error page), use generic message
          if (errorText.includes('<html>') || errorText.includes('502 Bad Gateway')) {
            errorMessage = 'Backend service is temporarily unavailable. Please check if the Report Generation Service is running.';
          } else {
            // Try to parse as JSON
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error || errorData.details || errorMessage;
          }
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      // Try to extract error message from response
      let errorMessage = 'Failed to fetch available templates';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.details || errorMessage;
      } catch (e) {
        // If response is not JSON, use status text
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    
    // Ensure templates array exists
    if (!data.templates) {
      console.warn('Response missing templates array, returning empty array');
      return { ...data, templates: [] };
    }

    return data;
  } catch (error) {
    console.error('Error fetching available templates:', error);
    // Re-throw with more context if it's a network/502 error
    if (error.message.includes('502') || error.message.includes('Bad Gateway')) {
      throw new Error('Unable to connect to the template service. Please ensure the Report Generation Service is running and try again.');
    }
    throw error;
  }
};

/**
 * Get a single template by ID
 */
export const getTemplateById = async (templateId) => {
  try {
    const response = await apifetch(`${API_BASE_URL}/templates/${templateId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch template');
    }

    const data = await response.json();
    return data.template;
  } catch (error) {
    console.error('Error fetching template:', error);
    throw error;
  }
};

/**
 * Create a new template
 */
export const createTemplate = async (templateData) => {
  try {
    const response = await apifetch(`${API_BASE_URL}/templates`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(templateData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create template');
    }

    const data = await response.json();
    return data.template;
  } catch (error) {
    console.error('Error creating template:', error);
    throw error;
  }
};

/**
 * Update an existing template
 */
export const updateTemplate = async (templateId, updateData) => {
  try {
    const response = await apifetch(`${API_BASE_URL}/templates/${templateId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update template');
    }

    const data = await response.json();
    return data.template;
  } catch (error) {
    console.error('Error updating template:', error);
    throw error;
  }
};

/**
 * Delete a template
 */
export const deleteTemplate = async (templateId) => {
  try {
    const response = await apifetch(`${API_BASE_URL}/templates/${templateId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete template');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
};

/**
 * Clone a template
 */
export const cloneTemplate = async (templateId, newName) => {
  try {
    const response = await apifetch(`${API_BASE_URL}/templates/${templateId}/clone`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ name: newName })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to clone template');
    }

    const data = await response.json();
    return data.template;
  } catch (error) {
    console.error('Error cloning template:', error);
    throw error;
  }
};

/**
 * Get default configuration for a base template
 */
export const getDefaultConfiguration = async (baseTemplate) => {
  try {
    const response = await apifetch(`${API_BASE_URL}/templates/defaults/${baseTemplate}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch default configuration');
    }

    const data = await response.json();
    return data.configuration;
  } catch (error) {
    console.error('Error fetching default configuration:', error);
    throw error;
  }
};

/**
 * Export report with selected template
 */
export const exportReport = async (fileUrl, templateId, format = 'pdf', reportType = 'detail') => {
  try {
    const response = await apifetch(`${API_BASE_URL}/export`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        fileUrl,
        template_id: templateId,
        format,
        reportType
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to export report' }));
      throw new Error(errorData.error || 'Failed to export report');
    }

    return await response.blob();
  } catch (error) {
    console.error('Error exporting report:', error);
    throw error;
  }
};

/**
 * Utility: Check if a template is a custom template
 * @param {Object} template - Template object
 * @returns {boolean} True if template is custom
 */
export const isCustomTemplate = (template) => {
  if (!template || !template.id) return false;
  
  const idStr = String(template.id);
  
  // If ID starts with 'custom_', it's definitely custom
  if (idStr.startsWith('custom_')) return true;
  
  // Check explicit flags
  if (template.isCustom === true) return true;
  if (template.is_default === false) return true;
  
  // If is_default is explicitly true, it's not custom
  if (template.is_default === true) return false;
  
  // For numeric IDs without explicit is_default flag, assume custom
  const numericId = parseInt(idStr.replace('custom_', ''), 10);
  if (!isNaN(numericId)) return true;
  
  return false;
};

/**
 * Utility: Normalize template ID for consistent handling
 * Returns ID with 'custom_' prefix for custom templates
 * @param {Object|string|number} templateOrId - Template object or ID
 * @returns {string|null} Normalized template ID
 */
export const normalizeTemplateId = (templateOrId) => {
  if (!templateOrId) return null;
  
  // If it's already a string ID, check if it needs normalization
  if (typeof templateOrId === 'string' || typeof templateOrId === 'number') {
    const idStr = String(templateOrId);
    
    // Already prefixed, return as-is
    if (idStr.startsWith('custom_')) return idStr;
    
    // For raw numeric IDs without template context, return as-is
    // (the backend handles both prefixed and unprefixed IDs)
    return idStr;
  }
  
  // If it's a template object, use is_default flag to determine prefix
  if (typeof templateOrId === 'object') {
    const template = templateOrId;
    const idStr = String(template.id);
    
    // Already prefixed, return as-is
    if (idStr.startsWith('custom_')) return idStr;
    
    // Check if it's a custom template (uses is_default flag)
    if (isCustomTemplate(template)) {
      return `custom_${idStr.replace('custom_', '')}`;
    }
    
    return idStr;
  }
  
  return null;
};

/**
 * Utility: Get numeric ID from template ID (removes 'custom_' prefix if present)
 * @param {string|number} templateId - Template ID (with or without prefix)
 * @returns {number|null} Numeric ID
 */
export const getNumericTemplateId = (templateId) => {
  if (!templateId) return null;
  
  const idStr = String(templateId);
  const numericPart = idStr.replace('custom_', '');
  const numericId = parseInt(numericPart, 10);
  
  return isNaN(numericId) ? null : numericId;
};
